import React from 'react';

import { addBreadcrumb } from '@/lib/errors';

import { ErrorBoundary } from './ErrorBoundary';

interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Route path for tracking (e.g., '/groups/[id]')
   */
  routePath?: string;
}

/**
 * Error boundary specifically for route-level errors in Next.js App Router
 * This component can be used in layout.tsx files to catch errors in entire route segments
 *
 * Enhanced with:
 * - Automatic route change breadcrumb tracking
 * - Route-specific error context
 */
export function RouteErrorBoundary({
  children,
  routePath,
}: RouteErrorBoundaryProps) {
  const handleError = (error: Error, _errorInfo: React.ErrorInfo) => {
    // Add route-specific breadcrumb
    addBreadcrumb({
      category: 'navigation',
      message: `Route error in ${routePath || window.location.pathname}`,
      level: 'error',
      data: {
        pathname:
          typeof window !== 'undefined' ? window.location.pathname : 'unknown',
        routePath,
        errorMessage: error.message,
      },
    });
  };

  return (
    <ErrorBoundary
      boundaryName={`RouteErrorBoundary:${routePath || 'unknown'}`}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
}

export default RouteErrorBoundary;
